import { useEffect, useMemo, useState } from "react";
import api, { money } from "../../../../lib/api";
import { toast } from "sonner";
import {
  Activity, AlertTriangle, ArrowRight, Calendar, Check, Clock, DollarSign,
  ExternalLink, FileText, Inbox, Info, MapPin, RefreshCw, ShieldCheck, Store,
  Truck, Upload, Users,
} from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../../../components/ui/select";
import SearchAndFilter from "../../../../components/SearchAndFilter";
import OrderIdTriple from "../../../dropship/OrderIdTriple";
import VerqoSmartShippingBar from "../../../buyer/VerqoSmartShippingBar";
import OrderDetailDrawer from "../fulfillment/OrderDetailDrawer";
import { EmptyState } from "../shared";

/**
 * DailyOrdersTab — the seller's day-to-day dropship order queue.
 * KPI header, filter chips, search+filter, order table, live tracking
 * dialog, and the carrier movement re-scan button.
 * Extracted from SellerDropship.jsx in Phase 8 refactor.
 */
export default function DailyOrdersTab({
  orders,
  movement,
  reload,
  onRescan,
  // Optional external selection state — when provided, selection is lifted
  // to the parent so a hosting workspace (e.g. FulfillmentCenterTab's Orders
  // sub-tab) can share the same Set with its own bulk-action bar.
  selectionSet,
  onSelectionSetChange,
  // Optional render-prop injected INTO the "N selected" bar so the parent
  // can add its own bulk-action buttons (e.g. Buy Labels via Verqo Shipping) next
  // to the built-in Export CSV / Clear buttons — keeps one unified bar.
  renderExtraBulkActions,
  // Optional render-prop that replaces the default customer/product cell
  // for a given row. Hosting workspaces (e.g. Fulfillment Center) pass a
  // richer HoverCard variant showing full ship-to address + item preview +
  // "Compare rates" quick action. When omitted, the built-in compact cell
  // (customer name · product name · tracking button · movement badge) is
  // rendered — good enough for the buyer app's read-only feed.
  renderCustomerCell,
  // Optional render-prop appended AFTER the built-in row actions (Ship with
  // Verqo / Manual / SLA / etc.) so the parent can inject an advanced
  // per-row dropdown (RowShipMenu — Rate compare, Tag, Split, QR return,
  // Duplicate, Combine) without duplicating the base actions.
  renderRowActionsExtra,
  // Optional ref object that the hosting workspace can use to trigger
  // internal actions (rescan · openCsvImport · exportCsv) from its own
  // header/toolbar. Populated on every render — read via
  // `actionsRef.current.rescan?.()`. Lets us hoist Rescan/Import/Export
  // buttons to the Fulfillment Center card header (Shopify pattern)
  // instead of inline with the filter tiles.
  actionsRef,
  // Optional callback fired whenever the internal rescanning state flips
  // — used by the parent to reflect the busy state on its own Rescan
  // button (spinning icon while a refresh is in flight).
  onRescanningChange,
}) {
  const [statusChip, setStatusChip] = useState("all");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({});
  const [rescanning, setRescanning] = useState(false);
  const [shipOpen, setShipOpen] = useState(null);
  // Iter 204 · Order detail side drawer state. Holds the order id of the
  // currently-open detail. Wired via ↑/↓ keyboard and row click.
  const [detailOid, setDetailOid] = useState(null);
  const [tracking, setTracking] = useState("");
  const [trackOpen, setTrackOpen] = useState(null);
  const [verqoShipOpen, setVerqoShipOpen] = useState(null);
  const [verqoShipBusy, setVerqoShipBusy] = useState(false);
  const [verqoShipWeight, setVerqoShipWeight] = useState(1);
  // Iter 240 · Post-ship success state — enables insurance upsell in-dialog
  const [verqoShipResult, setVerqoShipResult] = useState(null);
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  const [csvImportText, setCsvImportText] = useState("");
  const [csvImportBusy, setCsvImportBusy] = useState(false);
  const [csvImportResult, setCsvImportResult] = useState(null);
  // Bulk-select state: tracks which order IDs are ticked. When the parent
  // supplies `selectionSet`, we mirror the parent (lifted state); otherwise
  // we keep an internal Set. Cleared on reload and by the "Clear" button.
  const [internalSelected, setInternalSelected] = useState(() => new Set());
  const selected = selectionSet ?? internalSelected;
  const setSelected = (updater) => {
    if (onSelectionSetChange) {
      const next = typeof updater === "function" ? updater(selected) : updater;
      onSelectionSetChange(next);
    } else {
      setInternalSelected(updater);
    }
  };
  const today = new Date().toISOString().slice(0, 10);

  // ─── SLA Extension — request a grace window from the buyer ───
  const [slaReqOpen, setSlaReqOpen] = useState(null);
  const [slaReason, setSlaReason] = useState("stock_delay");
  const [slaReasonDetail, setSlaReasonDetail] = useState("");
  const [slaShipBy, setSlaShipBy] = useState("");
  const submitSlaRequest = async () => {
    if (!slaReasonDetail.trim() || slaReasonDetail.trim().length < 10) {
      toast.error("Please explain the delay in at least a sentence");
      return;
    }
    if (!slaShipBy) {
      toast.error("Pick a committed ship-by date");
      return;
    }
    try {
      await api.post(`/dropship/orders/${slaReqOpen.id}/sla-extension`, {
        reason_code: slaReason,
        reason_detail: slaReasonDetail.trim(),
        committed_ship_by: new Date(slaShipBy).toISOString(),
      });
      toast.success(`Extension requested for ${slaReqOpen.code} — buyer has 24h to respond`);
      setSlaReqOpen(null);
      setSlaReason("stock_delay");
      setSlaReasonDetail("");
      setSlaShipBy("");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit request");
    }
  };

  const isToday       = (o) => (o.created_at || "").slice(0, 10) === today;
  const isInTransit   = (o) => ["shipped", "in_transit"].includes(o.status);
  const isPending     = (o) => o.status === "forwarded";
  const ms = (o) => isInTransit(o) ? (o.movement_status || "moving") : null;
  const isStalled     = (o) => ms(o) === "stalled";
  const isNoMovement  = (o) => ms(o) === "no_movement";

  const buyerOptions = useMemo(() => {
    const seen = new Set();
    orders.forEach(o => { if (o.buyer_name) seen.add(o.buyer_name); });
    return [...seen].map(v => ({ value: v, label: v }));
  }, [orders]);
  const storeOptions = useMemo(() => {
    const seen = new Set();
    orders.forEach(o => { if (o.store_name) seen.add(o.store_name); });
    return [...seen].map(v => ({ value: v, label: v }));
  }, [orders]);

  const filtered = useMemo(() => {
    let xs = orders;
    if (statusChip === "today")        xs = xs.filter(isToday);
    if (statusChip === "in_transit")   xs = xs.filter(isInTransit);
    if (statusChip === "stalled")      xs = xs.filter(isStalled);
    if (statusChip === "no_movement")  xs = xs.filter(isNoMovement);
    if (statusChip === "pending")      xs = xs.filter(isPending);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      xs = xs.filter(o =>
        (o.code || "").toLowerCase().includes(q) ||
        (o.customer_name || "").toLowerCase().includes(q) ||
        (o.product_name || "").toLowerCase().includes(q) ||
        (o.buyer_name || "").toLowerCase().includes(q) ||
        (o.tracking || "").toLowerCase().includes(q) ||
        (o.buyer_external?.order_id || "").toLowerCase().includes(q) ||
        (o.buyer_external?.order_number || "").toLowerCase().includes(q) ||
        (o.seller_external?.order_id || "").toLowerCase().includes(q) ||
        (o.seller_external?.order_number || "").toLowerCase().includes(q)
      );
    }
    const F = filters || {};
    if (F.buyer?.values?.length)    xs = xs.filter(o => F.buyer.values.includes(o.buyer_name));
    if (F.store?.values?.length)    xs = xs.filter(o => F.store.values.includes(o.store_name));
    if (F.status?.values?.length)   xs = xs.filter(o => F.status.values.includes(o.status || "pending"));
    if (F.movement?.values?.length) xs = xs.filter(o => F.movement.values.includes(ms(o) || "none"));
    if (F.hasTracking?.on != null)  xs = xs.filter(o => F.hasTracking.on ? !!o.tracking : !o.tracking);
    if (F.hours?.value != null) {
      xs = xs.filter(o => {
        const v = Number(o.movement_hours);
        if (!Number.isFinite(v)) return false;
        if (F.hours.op === "gt") return v > Number(F.hours.value);
        if (F.hours.op === "lt") return v < Number(F.hours.value);
        if (F.hours.op === "between") return v >= Number(F.hours.min) && v <= Number(F.hours.max);
        return true;
      });
    }
    if (F.total?.value != null) {
      xs = xs.filter(o => {
        const v = (Number(o.wholesale_cost) || 0) * (Number(o.qty) || 0);
        if (F.total.op === "gt") return v > Number(F.total.value);
        if (F.total.op === "lt") return v < Number(F.total.value);
        if (F.total.op === "between") return v >= Number(F.total.min) && v <= Number(F.total.max);
        return true;
      });
    }
    if (F.date?.value) xs = xs.filter(o => (o.created_at || "") >= F.date.value);
    return xs;
  }, [orders, statusChip, search, filters, today]); // eslint-disable-line react-hooks/exhaustive-deps

  const chipCount = {
    today:       orders.filter(isToday).length,
    in_transit:  orders.filter(isInTransit).length,
    stalled:     orders.filter(isStalled).length,
    no_movement: orders.filter(isNoMovement).length,
    pending:     orders.filter(isPending).length,
  };

  // ─── Bulk export ────────────────────────────────────────────────────
  // Exports the selected orders (or all currently-filtered ones when nothing
  // is selected) as a CSV. Client-side generation — no backend endpoint
  // needed. Fields mirror what the seller actually sees in the row.
  const csvEscape = (v) => {
    if (v == null) return "";
    const s = String(v).replace(/\r?\n/g, " ");
    return /[",]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const exportCsv = () => {
    const rows = selected.size > 0
      ? orders.filter(o => selected.has(o.id))
      : filtered;
    if (rows.length === 0) {
      toast.error("Nothing to export");
      return;
    }
    const header = [
      "Order code",
      "Buyer order #",
      "Seller order #",
      "Placed",
      "Buyer",
      "Store",
      "Customer",
      "Ship to",
      "Product",
      "SKU",
      "Qty",
      "Wholesale",
      "Total",
      "Status",
      "Movement",
      "Hours since move",
      "Tracking",
      "Carrier",
    ];
    const lines = [header.join(",")];
    for (const o of rows) {
      const total = (Number(o.wholesale_cost) || 0) * (Number(o.qty) || 0);
      const st = o.ship_to || {};
      const shipTo = [st.name, st.city, st.state, st.country].filter(Boolean).join(" · ");
      lines.push([
        o.code,
        o.buyer_external?.order_number || o.buyer_external?.order_id || "",
        o.seller_external?.order_number || o.seller_external?.order_id || "",
        (o.created_at || "").slice(0, 19).replace("T", " "),
        o.buyer_name || "",
        o.store_name || "",
        o.customer_name || "",
        shipTo,
        o.product_name || "",
        o.sku || "",
        o.qty ?? "",
        (Number(o.wholesale_cost) || 0).toFixed(2),
        total.toFixed(2),
        o.status || "",
        ms(o) || "",
        o.movement_hours ?? "",
        o.tracking || "",
        o.carrier || "",
      ].map(csvEscape).join(","));
    }
    const blob = new Blob([lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.download = `dropship-orders-${stamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} order${rows.length === 1 ? "" : "s"} to CSV`);
  };

  // Bulk selection helpers
  const toggleSelected = (id) => setSelected(s => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const clearSelection = () => setSelected(new Set());
  const filteredIds = filtered.map(o => o.id);
  const allSelected  = filteredIds.length > 0 && filteredIds.every(id => selected.has(id));
  const someSelected = filteredIds.some(id => selected.has(id));
  const toggleAll = () => setSelected(s => {
    const n = new Set(s);
    if (allSelected) filteredIds.forEach(id => n.delete(id));
    else             filteredIds.forEach(id => n.add(id));
    return n;
  });

  const submitShip = async () => {
    if (!tracking.trim()) return toast.error("Enter a tracking number");
    try {
      await api.patch(`/dropship/orders/${shipOpen.id}/status`, null, {
        params: { status: "shipped", tracking: tracking.trim() },
      });
      toast.success(`${shipOpen.code} shipped — tracking pushed back to Shopify`);
      setShipOpen(null); setTracking(""); clearSelection(); reload();
    } catch { toast.error("Could not update"); }
  };

  // Tier 1 — Ship with Verqo. Auto-books cheapest carrier rate.
  const submitVerqoShip = async () => {
    if (!verqoShipOpen) return;
    setVerqoShipBusy(true);
    try {
      const { data } = await api.post(`/dropship/orders/${verqoShipOpen.id}/ship-with-verqo`,
        { weight_lb: Number(verqoShipWeight) || 1, mode: "parcel" });
      if (data.ok === false) {
        toast.error("Carrier declined the label — check ship-from address or contact support");
      } else {
        toast.success(`${verqoShipOpen.code} shipped via ${data.carrier} · ${data.tracking}`);
        // Iter 240 · Stay open to offer insurance upsell instead of closing.
        setVerqoShipResult(data);
        reload();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ship with Verqo failed");
    } finally { setVerqoShipBusy(false); }
  };

  // Tier 4 — CSV tracking import.
  const submitCsvImport = async () => {
    const txt = csvImportText.trim();
    if (!txt) return toast.error("Paste CSV rows or upload a file first");
    setCsvImportBusy(true);
    setCsvImportResult(null);
    try {
      // Client-side minimal parse — split into rows for the JSON payload.
      const lines = txt.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) return toast.error("CSV needs a header row + at least one data row");
      const header = lines[0].split(",").map(s => s.trim().toLowerCase());
      const codeIdx = header.findIndex(h => ["order_code", "order", "code"].includes(h));
      const trkIdx  = header.findIndex(h => ["tracking", "tracking_number"].includes(h));
      const carIdx  = header.findIndex(h => h === "carrier");
      if (codeIdx === -1 || trkIdx === -1) {
        return toast.error("CSV header must include `order_code` and `tracking` columns");
      }
      const rows = lines.slice(1).map(l => {
        const cells = l.split(",").map(s => s.trim().replace(/^"|"$/g, ""));
        return {
          order_code: cells[codeIdx] || "",
          tracking:   cells[trkIdx]  || "",
          carrier:    carIdx >= 0 ? (cells[carIdx] || null) : null,
        };
      }).filter(r => r.order_code && r.tracking);
      if (!rows.length) return toast.error("No usable rows found in the CSV");
      const { data } = await api.post("/dropship/orders/tracking-import", { rows });
      setCsvImportResult(data);
      if (data.updated > 0) {
        toast.success(`${data.updated} order${data.updated === 1 ? "" : "s"} shipped · ${data.skipped} skipped`);
      } else {
        toast.error(`Nothing was updated · ${data.skipped} skipped`);
      }
      if (data.updated > 0) reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "CSV import failed");
    } finally { setCsvImportBusy(false); }
  };

  const onCsvFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setCsvImportText(text);
    e.target.value = "";
  };

  // Download a minimal CSV template so sellers can open it in Excel /
  // Google Sheets, fill in rows, and re-upload. Filename mirrors the
  // export naming convention for consistency.
  // Iter 210 · added optional `channel` column for cross-channel
  // reconciliation — helps sellers filter/audit which channel each
  // tracking update belongs to (order_code is still the primary key).
  const downloadTemplateCsv = () => {
    const sample = [
      "order_code,tracking,carrier,channel",
      "DS-A1002,1ZA2W3X40012345678,UPS,shopify_dtc",
      "DS-A1003,9400123456781234567890,USPS,amazon",
      "DS-A1004,742038203945,FedEx,",
    ].join("\r\n");
    const blob = new Blob([sample], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "dropship-tracking-template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  const markDelivered = async (o) => {
    try { await api.patch(`/dropship/orders/${o.id}/status`, null, { params: { status: "delivered" }}); toast.success(`${o.code} marked delivered`); reload(); }
    catch { toast.error("Could not update"); }
  };

  const rescan = async () => {
    if (!onRescan) return;
    setRescanning(true);
    try { await onRescan(); toast.success("Movement re-scanned"); }
    catch { toast.error("Could not re-scan"); }
    finally { setRescanning(false); }
  };

  // Expose internal actions to the hosting workspace via ref-mutation so
  // the Fulfillment Center card header can host Rescan / Import / Export
  // buttons at the canonical Shopify-style top-right location. Populated
  // on every render — reading via `actionsRef.current.rescan?.()` is safe.
  if (actionsRef) {
    actionsRef.current = {
      rescan,
      openCsvImport: () => { setCsvImportOpen(true); setCsvImportResult(null); },
      exportCsv,
    };
  }
  // Notify parent when rescanning state changes so it can reflect the
  // spinning icon on its own Rescan button.
  useEffect(() => {
    if (onRescanningChange) onRescanningChange(rescanning);
  }, [rescanning, onRescanningChange]);

  return (
    <div className="space-y-3">
      {/* Sticky bulk-action bar — appears whenever any row is ticked.
          Hoisted OUTSIDE the table card so it can stick to the viewport top
          during long scrolls; renders regardless of empty state. */}
      {selected.size > 0 && (
        <div className="sticky top-3 z-20 rounded-2xl border border-foreground/20 bg-foreground text-background shadow-lg px-5 py-3 flex items-center gap-4 flex-wrap" data-testid="daily-orders-bulk-bar">
          <div className="text-[13px] font-medium">{selected.size} order{selected.size === 1 ? "" : "s"} selected</div>
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            {typeof renderExtraBulkActions === "function" && renderExtraBulkActions(
              filtered.filter(o => selected.has(o.id)),
              clearSelection,
            )}
            <button
              onClick={exportCsv}
              data-testid="daily-orders-bulk-export"
              className="rounded-full h-9 px-4 text-[12.5px] mono uppercase tracking-[0.14em] bg-background text-foreground hover:bg-background/90 inline-flex items-center gap-1.5"
            >
              <FileText className="w-3.5 h-3.5"/>Export CSV
            </button>
            <button
              onClick={clearSelection}
              data-testid="daily-orders-bulk-clear"
              className="text-[11.5px] mono uppercase tracking-[0.14em] text-background/70 hover:text-background ml-1"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Unified table card (Feb 2026 · Iter 201) — hosts three stacked
          strips + rows inside ONE bordered container so the segment
          selector visually reads as a "table tab-header", not just
          another loose chip row:
            1. Segment tab strip · full-width, underline-active
               (GitHub / Shopify Orders / Stripe Payments pattern)
            2. Search + facet bar · muted secondary bg for hierarchy
            3. Table header row · uppercase mono columns
            4. Order rows · one per order
          The strip stretches edge-to-edge and doesn't compete with the
          queue/draft chip rows above (which sit OUTSIDE this card). */}
      <div className="rounded-2xl border border-border bg-white overflow-hidden" data-testid="ds-orders-list">
        {/* Segment tab strip · full-width, no rounded pills. Active tab
            gets a solid-foreground bottom border; urgent tabs (rose/amber)
            keep tone via text color + subtle hover tint. */}
        <div className="flex items-stretch overflow-x-auto bg-white border-b border-border" data-testid="daily-filter-tiles">
          {[
            { k: "all",          l: "All",           c: orders.length,          tone: "neutral" },
            { k: "today",        l: "Today",         c: chipCount.today,        tone: "neutral" },
            { k: "in_transit",   l: "In transit",    c: chipCount.in_transit,   tone: "neutral" },
            { k: "stalled",      l: "Stalled",       c: chipCount.stalled,      tone: "amber",   hideIfZero: true },
            { k: "no_movement",  l: "No movement",   c: chipCount.no_movement,  tone: "rose",    hideIfZero: false },
            { k: "pending",      l: "Pending pack",  c: chipCount.pending,      tone: "neutral", hideIfZero: true },
          ].filter(f => !(f.hideIfZero && f.c === 0)).map(f => {
            const active = statusChip === f.k;
            const urgent = (f.tone === "rose" || f.tone === "amber") && f.c > 0;
            const activeBorder = active
              ? f.tone === "rose"  ? "border-rose-600"
              : f.tone === "amber" ? "border-amber-600"
              : "border-foreground"
              : "border-transparent";
            const textCls = active
              ? f.tone === "rose"  ? "text-rose-700"
              : f.tone === "amber" ? "text-amber-700"
              : "text-foreground"
              : urgent
                ? f.tone === "rose" ? "text-rose-700 hover:bg-rose-50/60" : "text-amber-700 hover:bg-amber-50/60"
                : "text-foreground/55 hover:text-foreground hover:bg-secondary/40";
            return (
              <button
                key={f.k}
                onClick={() => setStatusChip(f.k)}
                data-testid={`daily-filter-${f.k}`}
                title={
                  f.k === "stalled"      ? "No scans between 48–96h"
                  : f.k === "no_movement" ? "No scans in 96h+ · call the carrier before the buyer does"
                  : f.k === "pending"    ? "Mark shipped to push tracking to Shopify"
                  : undefined
                }
                className={`relative inline-flex items-center gap-2 whitespace-nowrap px-4 h-11 text-sm font-medium border-b-2 -mb-px transition-colors shrink-0 ${activeBorder} ${textCls}`}
              >
                {urgent && !active && <AlertTriangle className="w-3 h-3"/>}
                <span>{f.l}</span>
                <span className={`text-[11px] tabular-nums font-medium ${
                  active ? "opacity-70" : urgent ? "opacity-80" : "text-foreground/40"
                }`}>{f.c}</span>
              </button>
            );
          })}

          {/* Right-side spacer · absorbs remaining space so tabs stay
              left-aligned. Date + deadline tooltip removed (Feb 2026 ·
              Iter 202) — users already know the date, and the deadline
              copy is captured in the Fulfillment Center card title
              tooltip. Keeps the segment strip clean, Shopify-style. */}
          <div className="ml-auto" data-testid="daily-orders-actions" aria-hidden="true" />
        </div>

        {/* Search + facet bar · secondary-tinted background separates it
            visually from both the segment strip above and the table rows
            below without adding a heavy divider. */}
        <div className="px-4 py-2.5 border-b border-border bg-secondary/25">
          <SearchAndFilter
            page="seller_dropship_daily"
            search={search}
            onSearchChange={setSearch}
            filters={filters}
            onChange={setFilters}
            totalCount={filtered.length}
            facets={[
              { id: "buyer",       label: "Buyer",        icon: Users,      type: "multi", options: buyerOptions },
              { id: "store",       label: "Store",        icon: Store,      type: "multi", options: storeOptions },
              { id: "status",      label: "Status",       icon: Activity,   type: "multi", options: [
                { value: "forwarded",  label: "Pending pack" },
                { value: "shipped",    label: "Shipped" },
                { value: "in_transit", label: "In transit" },
                { value: "delivered",  label: "Delivered" },
                { value: "cancelled",  label: "Cancelled" },
              ]},
              { id: "movement",    label: "Movement",     icon: Truck,      type: "multi", options: [
                { value: "stalled",     label: "Stalled" },
                { value: "no_movement", label: "No movement" },
              ]},
              { id: "hasTracking", label: "Has tracking", icon: MapPin,     type: "boolean" },
              { id: "hours",       label: "Hours since move", icon: Clock,  type: "numeric" },
              { id: "total",       label: "Order total",  icon: DollarSign, type: "numeric", numericLabel: "$" },
              { id: "date",        label: "Placed after", icon: Calendar,   type: "date" },
            ]}
          />
        </div>

        {/* Empty state OR table rows */}
        {filtered.length === 0 ? (
          <div className="p-10">
            <EmptyState
              icon={Inbox}
              title={orders.length === 0 ? "Nothing to do" : "No orders match these filters"}
              body={orders.length === 0 ? "When a retailer forwards a customer order, it appears here for fulfilment." : "Clear a filter or chip above to see more orders."}
            />
          </div>
        ) : (
          <>
          <div className="grid grid-cols-[36px_150px_1fr_140px_80px_90px_200px] gap-3 px-5 py-2.5 bg-secondary/50 border-b border-border text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">
            <div className="flex items-center">
              <input
                type="checkbox"
                ref={el => { if (el) el.indeterminate = !allSelected && someSelected; }}
                checked={allSelected}
                onChange={toggleAll}
                data-testid="daily-orders-select-all"
                aria-label="Select all filtered orders"
                className="w-4 h-4 cursor-pointer accent-foreground"
              />
            </div>
            <div>Order · External IDs</div><div>Customer · Product</div><div>Buyer</div><div className="text-right">Qty</div><div className="text-right">Total</div><div className="text-right">Status · Movement</div>
          </div>
          <div className="divide-y divide-border">
            {filtered.map(o => {
              const total = (o.wholesale_cost || 0) * (o.qty || 0);
              const mv = ms(o);
              const slaExt = o.sla_extension;
              const isSlaApproved = slaExt?.status === "approved";
              const isSlaPending  = slaExt?.status === "pending";
              const isSlaRejected = slaExt?.status === "rejected";
              const rowTone =
                isSlaApproved         ? "bg-emerald-50/40 border-l-4 border-l-emerald-500" :
                isSlaPending          ? "bg-amber-50/30 border-l-4 border-l-amber-400" :
                mv === "no_movement"  ? "bg-rose-50 border-l-4 border-l-rose-500" :
                mv === "stalled"      ? "bg-amber-50/50 border-l-4 border-l-amber-500" : "";
              const hrs = o.movement_hours;
              return (
                <div
                  key={o.id}
                  onClick={(e) => {
                    // Ignore clicks on interactive descendants (buttons,
                    // inputs, links, dropdown triggers). Only bare-row
                    // clicks open the detail drawer.
                    if (e.target.closest("button, input, a, [role='menuitem'], [role='button']")) return;
                    setDetailOid(o.id);
                  }}
                  data-testid={`ds-order-${o.code}`}
                  className={`grid grid-cols-[36px_150px_1fr_140px_80px_90px_220px] gap-3 px-5 py-3 items-center text-[13px] cursor-pointer hover:bg-secondary/30 ${rowTone} ${selected.has(o.id) ? "bg-indigo-50/40" : ""}`}
                >
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selected.has(o.id)}
                      onChange={() => toggleSelected(o.id)}
                      data-testid={`daily-orders-select-${o.code}`}
                      aria-label={`Select order ${o.code}`}
                      className="w-4 h-4 cursor-pointer accent-foreground"
                    />
                  </div>
                  <OrderIdTriple order={o} role="seller"/>
                  {typeof renderCustomerCell === "function" ? (
                    renderCustomerCell(o)
                  ) : (
                  <div className="min-w-0">
                    <div className="font-medium truncate">{o.customer_name}</div>
                    <div className="text-[11px] text-foreground/55 truncate flex items-center gap-1.5 flex-wrap">
                      <span className="truncate">{o.product_name}</span>
                      {o.tracking && (
                        <button onClick={() => setTrackOpen(o)}
                          data-testid={`seller-track-${o.code}`}
                          title="View live carrier tracking"
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-border bg-white hover:bg-secondary transition-colors text-foreground align-middle mono text-[10.5px]">
                          <MapPin className="w-2.5 h-2.5 text-foreground/60"/>
                          {o.tracking}
                        </button>
                      )}
                      {mv && mv !== "moving" && hrs != null && !isSlaApproved && (
                        <span className={`mono text-[10px] ${mv === "no_movement" ? "text-rose-700" : "text-amber-700"}`}>
                          · last move {hrs}h ago
                        </span>
                      )}
                      {isSlaApproved && (
                        <span className="text-[10px] mono text-emerald-700">
                          · ship-by extended to {new Date(slaExt.committed_ship_by).toLocaleDateString()} by {slaExt.responded_by_name}
                        </span>
                      )}
                      {isSlaPending && (
                        <span className="text-[10px] mono text-amber-700">
                          · waiting on buyer · expires in {Math.max(0, Math.round((new Date(slaExt.expires_at) - Date.now()) / 3600000))}h
                        </span>
                      )}
                    </div>
                  </div>
                  )}
                  <div className="truncate">{o.buyer_name}</div>
                  <div className="text-right">{o.qty}</div>
                  <div className="text-right font-semibold">{money(total)}</div>
                  <div className="flex items-center justify-end gap-1">
                    {o.status === "forwarded" ? (
                      <>
                        <Button onClick={() => { setVerqoShipOpen(o); setVerqoShipWeight(1); }}
                          data-testid={`verqo-ship-${o.code}`}
                          title="Book Verqo carrier · claim-eligible"
                          className="rounded-full h-8 px-3 text-[11px] bg-foreground text-background hover:bg-foreground/90">
                          <ShieldCheck className="w-3 h-3 mr-1"/>Ship with Verqo
                        </Button>
                        <button onClick={() => { setShipOpen(o); setTracking(""); }}
                          data-testid={`ship-${o.code}`}
                          title="Enter tracking manually (external carrier)"
                          className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-8 px-3 border border-border text-foreground/70 hover:text-foreground hover:border-foreground/40 inline-flex items-center gap-1">
                          <Truck className="w-3 h-3"/>Manual
                        </button>
                      </>
                    ) : isSlaApproved ? (
                      <span data-testid={`sla-approved-${o.code}`}
                        className="inline-flex items-center gap-1 text-[10.5px] mono uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                        <ShieldCheck className="w-3 h-3"/>SLA extended
                      </span>
                    ) : isSlaPending ? (
                      <span data-testid={`sla-pending-${o.code}`}
                        className="inline-flex items-center gap-1 text-[10.5px] mono uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                        <Clock className="w-3 h-3"/>Ext pending
                      </span>
                    ) : mv === "no_movement" ? (
                      <>
                        <span data-testid={`movement-${o.code}`}
                          className="inline-flex items-center gap-1 text-[10.5px] mono uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border bg-rose-100 text-rose-700 border-rose-200">
                          <AlertTriangle className="w-3 h-3"/>No movement
                        </span>
                        <button onClick={() => { setSlaReqOpen(o); setSlaShipBy(new Date(Date.now() + 2*86400000).toISOString().slice(0,10)); }}
                          data-testid={`sla-request-${o.code}`}
                          title="Ask buyer to grant you grace on the ship-by deadline"
                          className="inline-flex items-center gap-1 rounded-full h-7 px-2 text-[10.5px] mono uppercase tracking-[0.14em] border border-foreground bg-foreground text-background hover:bg-foreground/90">
                          <Clock className="w-3 h-3"/>Request ext
                        </button>
                      </>
                    ) : mv === "stalled" ? (
                      <>
                        <span data-testid={`movement-${o.code}`}
                          className="inline-flex items-center gap-1 text-[10.5px] mono uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border bg-amber-100 text-amber-700 border-amber-200">
                          <Clock className="w-3 h-3"/>Stalled
                        </span>
                        <button onClick={() => { setSlaReqOpen(o); setSlaShipBy(new Date(Date.now() + 2*86400000).toISOString().slice(0,10)); }}
                          data-testid={`sla-request-${o.code}`}
                          title="Ask buyer to grant you grace on the ship-by deadline"
                          className="inline-flex items-center gap-1 rounded-full h-7 px-2 text-[10.5px] mono uppercase tracking-[0.14em] border border-border hover:bg-secondary">
                          <Clock className="w-3 h-3"/>Request ext
                        </button>
                      </>
                    ) : o.status === "shipped" || o.status === "in_transit" ? (
                      <>
                        <span className="text-[10.5px] mono uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border bg-indigo-50 text-indigo-700 border-indigo-200">
                          {o.status}
                        </span>
                        <button onClick={() => markDelivered(o)} data-testid={`deliver-${o.code}`}
                          title="Mark delivered"
                          className="inline-flex items-center gap-1 rounded-full h-7 px-2 text-[10.5px] mono uppercase tracking-[0.14em] border border-border hover:bg-secondary">
                          <Check className="w-3 h-3"/>Delivered
                        </button>
                      </>
                    ) : isSlaRejected ? (
                      <span className="inline-flex items-center gap-1 text-[10.5px] mono uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">
                        <AlertTriangle className="w-3 h-3"/>Ext rejected
                      </span>
                    ) : (
                      <span className={`text-[10.5px] mono uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${o.status === "delivered" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-secondary text-foreground/55 border-border"}`}>
                        {o.status || "pending"}
                      </span>
                    )}
                    {typeof renderRowActionsExtra === "function" && renderRowActionsExtra(o)}
                  </div>
                </div>
              );
            })}
          </div>
          </>
        )}
      </div>

      {/* Ship dialog — unchanged from original (manual tracking entry) */}
      <Dialog open={!!shipOpen} onOpenChange={(v) => !v && setShipOpen(null)}>
        <DialogContent className="max-w-md">
          {shipOpen && (
            <>
              <DialogHeader><DialogTitle>Mark shipped — {shipOpen.code}</DialogTitle></DialogHeader>
              <p className="text-sm text-foreground/55 -mt-1">Enter the tracking number from your external carrier. Verqo insurance + claim backing don't apply to manually-tracked shipments.</p>
              <div className="mt-4 rounded-xl bg-secondary/40 border border-border p-3 text-xs space-y-1">
                <div><span className="text-foreground/55">To:</span> <span className="font-medium">{shipOpen.customer_name}</span></div>
                <div><span className="text-foreground/55">Address:</span> {shipOpen.customer_address}</div>
                <div><span className="text-foreground/55">Item:</span> {shipOpen.product_name} × {shipOpen.qty}</div>
              </div>
              <Input value={tracking} onChange={e => setTracking(e.target.value)} placeholder="1ZA2W3X40012345678" data-testid="ds-tracking-input" className="mt-4 h-11 mono"/>
              <Button onClick={submitShip} className="mt-3 w-full h-11 rounded-full bg-foreground text-background hover:bg-foreground/90" data-testid="ds-ship-confirm">
                Mark shipped <ArrowRight className="w-4 h-4 ml-1.5"/>
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ─── Tier 1 · Ship with Verqo dialog ─────────────────────────── */}
      <Dialog open={!!verqoShipOpen} onOpenChange={(v) => !v && !verqoShipBusy && (setVerqoShipOpen(null), setVerqoShipResult(null))}>
        <DialogContent className="max-w-md" data-testid="verqo-ship-dialog">
          {verqoShipOpen && !verqoShipResult && (
            <>
              <DialogHeader>
                <DialogTitle>Ship with Verqo — {verqoShipOpen.code}</DialogTitle>
              </DialogHeader>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 flex gap-2 text-[12px] text-emerald-900">
                <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5"/>
                <div>
                  <div className="font-semibold">Claim + insurance protected</div>
                  <div className="mt-0.5">Verqo brokers the label, handles claims for lost / damaged / late packages, and passes the credit back on your daily invoice. Only available when you ship through Verqo.</div>
                </div>
              </div>
              <div className="mt-3 rounded-xl bg-secondary/40 border border-border p-3 text-xs space-y-1">
                <div><span className="text-foreground/55">To:</span> <span className="font-medium">{verqoShipOpen.customer_name}</span></div>
                <div><span className="text-foreground/55">Address:</span> {verqoShipOpen.customer_address}</div>
                <div><span className="text-foreground/55">Item:</span> {verqoShipOpen.product_name} × {verqoShipOpen.qty}</div>
              </div>
              <div className="mt-3">
                <label className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 block mb-1">Package weight (lb)</label>
                <div className="relative">
                  <Input type="number" min="0.1" step="0.1" max="150"
                    value={verqoShipWeight}
                    onChange={e => setVerqoShipWeight(e.target.value)}
                    data-testid="verqo-ship-weight"
                    className="h-11 mono pl-3 pr-14"/>
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] mono text-foreground/55">lb</span>
                </div>
                <div className="text-[11px] text-foreground/55 mt-1">We&#39;ll auto-book the cheapest recommended carrier for a 10&quot;×8&quot;×4&quot; parcel at this weight.</div>
              </div>
              <Button onClick={submitVerqoShip} disabled={verqoShipBusy}
                data-testid="verqo-ship-confirm"
                className="mt-4 w-full h-11 rounded-full bg-foreground text-background hover:bg-foreground/90 disabled:opacity-60">
                {verqoShipBusy ? "Booking label…" : (<>Buy label &amp; mark shipped <ArrowRight className="w-4 h-4 ml-1.5"/></>)}
              </Button>
            </>
          )}
          {verqoShipOpen && verqoShipResult && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-emerald-600"/>
                  Label bought · {verqoShipOpen.code}
                </DialogTitle>
              </DialogHeader>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-[12px] text-emerald-900">
                <div className="font-semibold">Shipped via {verqoShipResult.carrier}</div>
                <div className="mono text-[11px] mt-1">Tracking: {verqoShipResult.tracking}</div>
              </div>
              {/* Iter 240 · Post-ship insurance upsell — Verqo Shipping */}
              <div className="mt-3">
                <VerqoSmartShippingBar
                  mode="booked"
                  trackingCode={verqoShipResult.tracking}
                  carrier={verqoShipResult.carrier}
                  reference={verqoShipOpen.id}
                  declaredValue={Number(verqoShipOpen.wholesale_cost) || Number(verqoShipOpen.subtotal) || 100}
                />
              </div>
              <Button
                onClick={() => { setVerqoShipOpen(null); setVerqoShipResult(null); }}
                data-testid="verqo-ship-done"
                className="mt-3 w-full h-10 rounded-full">
                Done
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ─── Tier 4 · Import tracking dialog ─────────────────────────── */}
      <Dialog open={csvImportOpen} onOpenChange={(v) => !v && !csvImportBusy && (setCsvImportOpen(false), setCsvImportText(""), setCsvImportResult(null))}>
        <DialogContent className="max-w-2xl" data-testid="csv-import-dialog">
          <DialogHeader>
            <DialogTitle>Import tracking numbers</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-foreground/60 -mt-1">Bulk-mark orders shipped from your own carrier / ERP. Paste CSV rows or upload a file with an <code className="text-[11px] bg-secondary px-1 rounded">order_code, tracking, carrier</code> header row.</p>

          {/* CSV template + example — sellers copy-paste or download */}
          <div className="rounded-xl border border-border bg-secondary/40 p-3 mt-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/60">Template · expected format</div>
              <button
                type="button"
                onClick={downloadTemplateCsv}
                data-testid="csv-import-download-template"
                className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-7 px-2.5 border border-border bg-white hover:bg-background hover:text-foreground inline-flex items-center gap-1"
              >
                <FileText className="w-3 h-3"/>Download template
              </button>
            </div>
            <div className="rounded-md border border-border bg-white overflow-hidden text-[11px] mono">
              <div className="grid grid-cols-[130px_1fr_100px_130px] gap-0 bg-secondary/60 border-b border-border">
                <div className="px-2.5 py-1.5 border-r border-border text-foreground/60 uppercase tracking-[0.14em]">order_code</div>
                <div className="px-2.5 py-1.5 border-r border-border text-foreground/60 uppercase tracking-[0.14em]">tracking</div>
                <div className="px-2.5 py-1.5 border-r border-border text-foreground/60 uppercase tracking-[0.14em]">carrier</div>
                <div className="px-2.5 py-1.5 text-foreground/60 uppercase tracking-[0.14em]">channel</div>
              </div>
              {[
                ["DS-A1002", "1ZA2W3X40012345678",     "UPS",   "shopify_dtc"],
                ["DS-A1003", "9400123456781234567890", "USPS",  "amazon"],
                ["DS-A1004", "742038203945",           "FedEx", ""],
              ].map(([c, t, cr, ch]) => (
                <div key={c} className="grid grid-cols-[130px_1fr_100px_130px] gap-0 border-b border-border last:border-b-0">
                  <div className="px-2.5 py-1.5 border-r border-border text-foreground/80">{c}</div>
                  <div className="px-2.5 py-1.5 border-r border-border text-foreground/80">{t}</div>
                  <div className="px-2.5 py-1.5 border-r border-border text-foreground/80">{cr}</div>
                  <div className="px-2.5 py-1.5 text-foreground/60">{ch || "—"}</div>
                </div>
              ))}
            </div>
            <div className="text-[11px] text-foreground/60 mt-2 leading-relaxed">
              • <strong>order_code</strong> — the code shown at the start of each order row (e.g. <code className="text-[10.5px] bg-secondary px-1 rounded">DS-A1002</code>).<br/>
              • <strong>tracking</strong> — carrier tracking number (any format).<br/>
              • <strong>carrier</strong> — optional. Examples: UPS · USPS · FedEx · DHL · OnTrac.<br/>
              • <strong>channel</strong> — optional. For reconciliation only. Examples: <code className="text-[10.5px] bg-secondary px-1 rounded">shopify_dtc</code> · <code className="text-[10.5px] bg-secondary px-1 rounded">amazon</code> · <code className="text-[10.5px] bg-secondary px-1 rounded">tiktok</code> · <code className="text-[10.5px] bg-secondary px-1 rounded">walmart</code> · <code className="text-[10.5px] bg-secondary px-1 rounded">generic_wholesale</code>.
            </div>
          </div>

          {/* Iter 210 · Redirect for NEW-order imports.
              This dialog only updates tracking on existing orders.
              Sellers who want to import net-new orders (with proper
              channel attribution) belong in the channel-scoped importer. */}
          <div className="mt-3 rounded-lg border border-violet-200 bg-violet-50 text-violet-800 px-3 py-2 text-[11.5px] flex items-start gap-2">
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5"/>
            <div>
              <span className="font-semibold">Importing NEW orders (not tracking)?</span>{" "}
              Head to <strong>Store setup → Direct sales channels</strong> and click{" "}
              <strong>Manage</strong> on the channel the orders came from.
              That keeps every order tagged with its source channel so revenue
              reconciles per-marketplace automatically.
            </div>
          </div>

          <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 flex gap-2 text-[12px] text-amber-900 mt-3">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5"/>
            <div>
              <strong>No claim coverage on imported tracking.</strong> Orders shipped outside Verqo carrier don&#39;t qualify for the automatic claim + insurance flow. For claim-backed shipping, use <em>Ship with Verqo</em>.
            </div>
          </div>
          <div className="mt-3">
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55">CSV data</label>
              <label className="text-[11px] mono uppercase tracking-[0.14em] text-foreground/60 hover:text-foreground cursor-pointer inline-flex items-center gap-1">
                <Upload className="w-3 h-3"/> Upload CSV
                <input type="file" accept=".csv,text/csv" onChange={onCsvFile}
                  data-testid="csv-import-file" className="hidden"/>
              </label>
            </div>
            <textarea
              value={csvImportText}
              onChange={e => setCsvImportText(e.target.value)}
              rows={7}
              data-testid="csv-import-text"
              placeholder={"order_code,tracking,carrier\nDS-A1002,1ZA2W3X40012345678,UPS\nDS-A1003,9400123456781234567890,USPS"}
              className="w-full rounded-lg border border-border bg-white p-3 mono text-[12px] focus:outline-none focus:ring-2 focus:ring-foreground/10"
            />
          </div>

          {csvImportResult && (
            <div className="mt-3 space-y-2" data-testid="csv-import-result">
              <div className="flex items-center gap-3 text-[12px]">
                <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 bg-emerald-100 text-emerald-800 border border-emerald-200 mono uppercase tracking-[0.14em] text-[10.5px]">
                  <Check className="w-3 h-3"/> {csvImportResult.updated} shipped
                </span>
                {csvImportResult.skipped > 0 && (
                  <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 bg-secondary text-foreground/70 border border-border mono uppercase tracking-[0.14em] text-[10.5px]">
                    <AlertTriangle className="w-3 h-3"/> {csvImportResult.skipped} skipped
                  </span>
                )}
              </div>
              {csvImportResult.skipped_rows?.length > 0 && (
                <div className="rounded-xl border border-border bg-secondary/40 max-h-40 overflow-y-auto text-[11.5px]">
                  {csvImportResult.skipped_rows.map((r, i) => (
                    <div key={i} className="px-3 py-1.5 border-b border-border last:border-b-0">
                      <span className="mono text-foreground/80">{r.order_code}</span>
                      <span className="text-foreground/55"> — {r.message}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="mt-4 flex items-center justify-end gap-2">
            <button
              onClick={() => { setCsvImportOpen(false); setCsvImportText(""); setCsvImportResult(null); }}
              disabled={csvImportBusy}
              className="text-[11.5px] mono uppercase tracking-[0.14em] rounded-full h-9 px-4 text-foreground/70 hover:text-foreground disabled:opacity-50"
            >
              Close
            </button>
            <Button onClick={submitCsvImport} disabled={csvImportBusy || !csvImportText.trim()}
              data-testid="csv-import-submit"
              className="rounded-full h-9 px-4 text-[11.5px] mono uppercase tracking-[0.14em] bg-foreground text-background hover:bg-foreground/90 disabled:opacity-60">
              {csvImportBusy ? "Importing…" : "Import tracking"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Live tracking dialog — shared across both roles */}
      <LiveTrackingDialog order={trackOpen} onClose={() => setTrackOpen(null)}/>

      {/* Iter 204 · Order detail side drawer · Linear/Superhuman style.
          Opens on row click, supports ↑/↓ nav across filtered orders,
          Esc to close. Actions wire back to the existing Ship/Manual/
          Deliver/SLA handlers so behaviour matches inline row buttons. */}
      <OrderDetailDrawer
        order={detailOid ? filtered.find(o => o.id === detailOid) : null}
        siblings={filtered}
        onClose={() => setDetailOid(null)}
        onNavigate={(o) => setDetailOid(o.id)}
        onAction={{
          ship:        (o) => setVerqoShipOpen(o),
          manualTrack: (o) => setShipOpen(o),
          deliver:     (o) => markDelivered(o),
          slaRequest:  (o) => {
            // Row-level SLA request already lives inline as `sla-request-${code}`
            // buttons. Trigger via a synthetic click so we reuse the exact
            // same code path (validation, toast, reload) without duplicating.
            const btn = document.querySelector(`[data-testid="sla-request-${o.code}"]`);
            if (btn) btn.click();
            else toast.error("SLA request unavailable for this order");
          },
        }}
      />

      {/* ─── SLA Extension request (seller side) — PREVIEW mock ─── */}
      <Dialog open={!!slaReqOpen} onOpenChange={v => !v && setSlaReqOpen(null)}>
        <DialogContent className="max-w-lg">
          {slaReqOpen && (
            <>
              <DialogHeader>
                <DialogTitle>Request SLA extension — {slaReqOpen.code}</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-foreground/60 -mt-1">
                Ask <span className="font-medium text-foreground/85">{slaReqOpen.buyer_name}</span> to grant you grace on the ship-by deadline. If approved, the order counts as on-time and no late-delivery claim can be filed against it.
              </p>
              <div className="mt-4 space-y-4">
                <div>
                  <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55 mb-1.5">Reason</div>
                  <Select value={slaReason} onValueChange={setSlaReason}>
                    <SelectTrigger data-testid="sla-reason-select" className="h-10 rounded-xl"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="stock_delay">Stock / restock delay</SelectItem>
                      <SelectItem value="carrier_issue">Carrier disruption</SelectItem>
                      <SelectItem value="packaging_delay">Packaging / QC delay</SelectItem>
                      <SelectItem value="weather">Weather · force majeure</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55 mb-1.5">Tell your buyer what's going on</div>
                  <textarea value={slaReasonDetail} onChange={e => setSlaReasonDetail(e.target.value)}
                    data-testid="sla-reason-detail"
                    rows={3}
                    placeholder="e.g., Primary carrier's route disrupted; re-booking with UPS Ground. ETA 2 days."
                    className="w-full rounded-xl border border-border bg-white px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-foreground/10"/>
                </div>
                <div>
                  <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55 mb-1.5">Your new committed ship-by</div>
                  <Input type="date" value={slaShipBy} onChange={e => setSlaShipBy(e.target.value)}
                    data-testid="sla-ship-by" className="h-10 mono"
                    min={new Date().toISOString().slice(0,10)}/>
                </div>
                <div className="rounded-xl border border-amber-200 bg-amber-50/60 px-3 py-2.5 text-[11.5px] text-amber-900 flex items-start gap-2">
                  <Clock className="w-3.5 h-3.5 mt-0.5 shrink-0"/>
                  <div>
                    The buyer has <span className="font-medium">24 hours</span> to approve or reject. If they don't respond in time the request expires and SLA stays breached. One extension max per order.
                  </div>
                </div>
              </div>
              <Button onClick={submitSlaRequest} data-testid="sla-submit"
                className="mt-4 w-full h-11 rounded-full bg-foreground text-background hover:bg-foreground/90">
                Submit to buyer <ArrowRight className="w-4 h-4 ml-1.5"/>
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// LiveTrackingDialog — carrier response + our internal event log timeline.
// Only DailyOrdersTab currently consumes it; co-located here.
// ────────────────────────────────────────────────────────────────────────
// KPI tile helper — small, reusable


// ─────────────────────────────────────────────────────────────────────
// Live carrier tracking dialog — renders the carrier response + our
// internal event log as one unified timeline. Opens when the seller clicks
// a tracking number in the Tracking tab.
// ─────────────────────────────────────────────────────────────────────
function LiveTrackingDialog({ order, onClose }) {
  // Public: also exported as a named export at bottom for BuyerDropship consumers.
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = async () => {
    if (!order) return;
    setLoading(true); setErr(null);
    try {
      const r = await api.get(`/dropship/orders/${order.id}/live-tracking`);
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not fetch tracking");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (order) { setData(null); load(); } /* eslint-disable-next-line */ }, [order?.id]);

  // Merge internal events + live events into a single timeline sorted desc by date
  const timeline = useMemo(() => {
    if (!data) return [];
    const rows = [];
    (data.live?.events || []).forEach(e => rows.push({
      kind: "carrier",
      status: e.status,
      details: e.details,
      date: e.date,
      location: e.location ? [e.location.city, e.location.state, e.location.country].filter(Boolean).join(", ") : null,
    }));
    (data.internal_events || []).forEach(e => rows.push({
      kind: "internal",
      status: e.status,
      details: e.note,
      date: e.at,
      location: e.location || null,
    }));
    rows.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    return rows;
  }, [data]);

  const status = data?.live?.status || data?.order_status || "—";
  const statusDetails = data?.live?.status_details;
  const eta = data?.live?.eta;
  const lastMove = data?.last_movement_at;

  return (
    <Dialog open={!!order} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="live-tracking-dialog">
        {order && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Truck className="w-5 h-5"/>
                Live tracking — {order.code}
              </DialogTitle>
            </DialogHeader>

            {loading && <div className="text-sm text-foreground/60 py-6 text-center">Fetching live carrier status…</div>}
            {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3">{err}</div>}

            {data && !loading && (
              <div className="space-y-4">
                {/* Status card */}
                <div className="rounded-2xl bg-secondary/40 border border-border p-4">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                      <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">Current status</div>
                      <div className="display text-2xl mt-0.5 capitalize">{String(status).replace(/_/g, " ").toLowerCase()}</div>
                      {statusDetails && <div className="text-[13px] text-foreground/65 mt-1">{statusDetails}</div>}
                      {!statusDetails && data.live?.error && <div className="text-[12px] text-amber-700 mt-1">Carrier API: {data.live.error}</div>}
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">ETA</div>
                      <div className="text-[14px] font-medium">{eta ? new Date(eta).toLocaleDateString() : "—"}</div>
                      {lastMove && (
                        <div className="text-[10.5px] mono text-foreground/50 mt-1">
                          Last move {new Date(lastMove).toLocaleString()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Tracking meta row */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-[12.5px]">
                  <div className="rounded-xl border border-border bg-white p-3">
                    <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">Carrier</div>
                    <div className="mt-0.5 font-medium">{data.carrier_label}</div>
                    {data.carrier_url && (
                      <a href={data.carrier_url} target="_blank" rel="noreferrer"
                         className="mt-1 inline-flex items-center gap-1 text-[11.5px] text-foreground/65 hover:text-foreground"
                         data-testid="carrier-external-link">
                        Open on {data.carrier_label} <ExternalLink className="w-3 h-3"/>
                      </a>
                    )}
                  </div>
                  <div className="rounded-xl border border-border bg-white p-3">
                    <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">Tracking #</div>
                    <div className="mt-0.5 mono text-[12.5px]">{data.tracking_number}</div>
                  </div>
                  <div className="rounded-xl border border-border bg-white p-3">
                    <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">Destination</div>
                    <div className="mt-0.5 truncate">{data.customer_name}</div>
                    <div className="text-[11px] text-foreground/55 truncate">{data.customer_address}</div>
                  </div>
                </div>

                {/* Timeline */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Event timeline</div>
                    <button onClick={load} data-testid="live-tracking-refresh"
                      className="inline-flex items-center gap-1 text-[11.5px] text-foreground/65 hover:text-foreground">
                      <RefreshCw className="w-3 h-3"/> Refresh
                    </button>
                  </div>
                  {timeline.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-border bg-secondary/20 px-4 py-6 text-center text-[12.5px] text-foreground/55">
                      No events recorded yet.
                      {data.live?.error && <div className="mt-1 text-[11px]">Carrier hasn't scanned this label yet.</div>}
                    </div>
                  ) : (
                    <div className="rounded-xl border border-border bg-white divide-y divide-border max-h-[320px] overflow-y-auto" data-testid="tracking-timeline">
                      {timeline.map((e, i) => (
                        <div key={i} className="px-4 py-3 flex items-start gap-3 text-[12.5px]">
                          <div className={`mt-1 w-2 h-2 rounded-full shrink-0 ${e.kind === "carrier" ? "bg-indigo-500" : "bg-emerald-500"}`}/>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium capitalize">{String(e.status || "update").replace(/_/g, " ").toLowerCase()}</span>
                              <span className={`text-[9.5px] mono uppercase tracking-[0.16em] px-1.5 py-0.5 rounded-full border ${e.kind === "carrier" ? "bg-indigo-50 text-indigo-700 border-indigo-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                                {e.kind === "carrier" ? "Carrier" : "Internal"}
                              </span>
                            </div>
                            {e.details && <div className="text-foreground/65 mt-0.5">{e.details}</div>}
                            <div className="text-[11px] text-foreground/50 mt-0.5 flex items-center gap-2 flex-wrap">
                              {e.date && <span>{new Date(e.date).toLocaleString()}</span>}
                              {e.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3"/>{e.location}</span>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Named export so BuyerDropship (and any future consumer) can import
// this dialog without depending on DailyOrdersTab internals.
export { LiveTrackingDialog };
